import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';

const VehicleFormData = ({ vehicleData }) => {
  const { register, handleSubmit, reset, setValue, formState: { errors }, watch } = useForm();
  const rcImagePreview = watch("rcImage")?.[0] ? URL.createObjectURL(watch("rcImage")[0]) : null;
  const licenseImage = watch("licenseImage")?.[0] ? URL.createObjectURL(watch("licenseImage")[0]) : null;
  const aadharImage = watch("aadharImage")?.[0] ? URL.createObjectURL(watch("aadharImage")[0]) : null;
  const ownerAadharImagePreview = watch("ownerAadharImage")?.[0] ? URL.createObjectURL(watch("ownerAadharImage")[0]) : null;
  const ownerPanImagePreview = watch("ownerPanImage")?.[0] ? URL.createObjectURL(watch("ownerPanImage")[0]) : null;

  const onSubmit = async (data) => {
    try {
      const payload = {
        vehicleNumber: data.vehicleNumber,
        engineNo: data.engineNo,
        ChassisNo: data.ChassisNo,
        fullAddress: data.fullAddress,
        ownerName: data.ownerName,
        ownerPhoneNumber: data.ownerPhoneNumber,
        ownerAadharNumber: data.ownerAadharNumber,
        ownerAadharImage: null,
        ownerPanNumber: data.ownerPanNumber,
        ownerPanImage: null,
        driverName: data.driverName,
        driverPrimaryNumber: data.driverPrimaryNumber,
        driverAlternativeNumber: data.driverAlternativeNumber,
        licenseNumber: data.licenseNumber,
        licenseImage: null,
        rcImage: null,
        aadharNumber: data.aadharNumber,
        aadharImage: null,
        reference1Name: data.reference1Name,
        reference1PhoneNumber: data.reference1PhoneNumber,
        reference2Name: data.reference2Name,
        reference2PhoneNumber: data.reference2PhoneNumber,
      };

      const imageFields = [
        'ownerAadharImage',
        'ownerPanImage',
        'rcImage',
        'licenseImage',
        'aadharImage',
      ];

      const uploadToCloudinary = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "prayagproperties");

        const response = await fetch(
          "https://api.cloudinary.com/v1_1/djmyigser/image/upload",
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();
        if (!data.secure_url) {
          throw new Error("Failed to upload image to Cloudinary");
        }
        return data.secure_url;
      };

      // Validate and upload all images
      for (let field of imageFields) {
        const file = data[field]?.[0];
        if (file) {
          if (file.size > 2 * 1024 * 1024) {
            toast.error(`${field} is too large. Max size is 2MB.`);
            return;
          }
          const url = await uploadToCloudinary(file);
          payload[field] = url;
        }
      }

      // Validate required fields
      const requiredFields = ['vehicleNumber', 'engineNo', 'ChassisNo', 'fullAddress'];
      const missingFields = requiredFields.filter((field) => !payload[field]);
      if (missingFields.length > 0) {
        toast.error(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Submit to backend
      const response = await sendApiRequest("api/trucks/post", {
        bulkData: vehicleData,
        ...payload,
      }, "POST");

      if (response.status === 201) {
        toast.success('Vehicle Added Successfully');
        reset();
      } else {
        toast.error('Vehicle is not Added');
      }

    } catch (error) {
      console.error('Error uploading vehicle data:', error);
      toast.error('An error occurred while uploading vehicle data');
    }
  };


  // const onSubmit = async (data) => {
  //   console.log(data);
  //   const response = await sendApiRequest(
  //     "api/trucks/post",
  //     { bulkData: vehicleData, ...data },
  //     "POST"
  //   );
  //   if (response.status === 201) {
  //     toast.success('Vehicle Added Successfully');
  //     reset();
  //   } else {
  //     toast.error('Vehicle is not Added');
  //   }
  //   console.log(response);
  // };

  useEffect(() => {
    if (vehicleData) {
      console.log(vehicleData);
      setValue('vehicleNumber', vehicleData?.rc_number);
      setValue('ChassisNo', vehicleData?.vehicle_chasi_number);
      setValue('engineNo', vehicleData?.vehicle_engine_number);
      setValue('fullAddress', vehicleData?.present_address);
      setValue('ownerName', vehicleData?.owner_name);
    }
  }, [vehicleData, setValue]);

  return (
    <div className="min-h-screen mt-3 bg-gradient-to-br from-blue-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-4">
            <h2 className="text-2xl font-bold text-white">Vehicle Registration Form</h2>
            <p className="text-blue-100 mt-1">Please fill in the vehicle details below</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Vehicle Details Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Vehicle Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Vehicle Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                  <input
                    type="text"
                    {...register('vehicleNumber', { required: 'Vehicle Number is required' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter vehicle number"
                  />
                  {errors.vehicleNumber && <p className="mt-1 text-sm text-red-600">{errors.vehicleNumber.message}</p>}
                </div>

                {/* Engine Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Engine Number</label>
                  <input
                    type="text"
                    {...register('engineNo', { required: 'Engine Number is required' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter engine number"
                  />
                  {errors.engineNo && <p className="mt-1 text-sm text-red-600">{errors.engineNo.message}</p>}
                </div>

                {/* Chassis Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chassis Number</label>
                  <input
                    type="text"
                    {...register('ChassisNo', { required: 'Chassis Number is required' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter chassis number"
                  />
                  {errors.ChassisNo && <p className="mt-1 text-sm text-red-600">{errors.ChassisNo.message}</p>}
                </div>
              </div>
            </div>

            {/* Additional Details Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Owner Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                  <input
                    type="text"
                    {...register('ownerName', { required: 'Owner name is required' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter owner name"
                  />
                  {errors.ownerName && <p className="mt-1 text-sm text-red-600">{errors.ownerName.message}</p>}
                </div>
                {/* Owner Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner Phone Number</label>
                  <input
                    type="text"
                    {...register('ownerPhoneNumber', {
                      pattern: {
                        value: /^\d{10}$/,
                        message: 'Owner Phone Number must be 10 digits'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter owner phone number"
                  />
                  {errors.ownerPhoneNumber && <p className="mt-1 text-sm text-red-600">{errors.ownerPhoneNumber.message}</p>}
                </div>

                {/* Owner Aadhar Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner Aadhar Number</label>
                  <input
                    type="text"
                    {...register('ownerAadharNumber', {
                      pattern: {
                        value: /^\d{12}$/,
                        message: 'Owner Aadhar Number must be 12 digits'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter owner Aadhar number"
                  />
                  {errors.ownerAadharNumber && <p className="mt-1 text-sm text-red-600">{errors.ownerAadharNumber.message}</p>}
                </div>

                {/* Owner Aadhar Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Owner Aadhar</label>
                  <input
                    type="file"
                    {...register('ownerAadharImage')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    accept="image/*"
                  />
                  {errors.ownerAadharImage && <p className="mt-1 text-sm text-red-600">{errors.ownerAadharImage.message}</p>}
                  {ownerAadharImagePreview && <img src={ownerAadharImagePreview} alt="Owner Aadhar Preview" className="w-32 h-20 mt-2" />}
                </div>

                {/* Owner PAN Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner PAN Number</label>
                  <input
                    type="text"
                    {...register('ownerPanNumber', {
                      pattern: {
                        value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                        message: 'Enter valid PAN number (e.g., ABCDE1234F)'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter owner PAN number"
                  />
                  {errors.ownerPanNumber && <p className="mt-1 text-sm text-red-600">{errors.ownerPanNumber.message}</p>}
                </div>

                {/* Owner PAN Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Owner PAN</label>
                  <input
                    type="file"
                    {...register('ownerPanImage')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    accept="image/*"
                  />
                  {errors.ownerPanImage && <p className="mt-1 text-sm text-red-600">{errors.ownerPanImage.message}</p>}
                  {ownerPanImagePreview && <img src={ownerPanImagePreview} alt="Owner PAN Preview" className="w-32 h-20 mt-2" />}
                </div>

                {/* Full Address */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                  <textarea
                    {...register('fullAddress', { required: 'Full Address is required' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    rows="3"
                    placeholder="Enter full address"
                  />
                  {errors.fullAddress && <p className="mt-1 text-sm text-red-600">{errors.fullAddress.message}</p>}
                </div>
              </div>
            </div>

            {/* RC Details Upload */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">RC Details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload RC Image</label>
                <input
                  type="file"
                  {...register('rcImage')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  accept="image/*"
                />
                {errors.rcImage && <p className="mt-1 text-sm text-red-600">{errors.rcImage.message}</p>}
                {rcImagePreview && <img src={rcImagePreview} alt="RC Preview" className="w-32 h-20 mt-2" />}
              </div>
            </div>

            {/* Driver Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Driver Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Driver Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver Name</label>
                  <input
                    type="text"
                    {...register('driverName')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter driver name"
                  />
                  {errors.driverName && <p className="mt-1 text-sm text-red-600">{errors.driverName.message}</p>}
                </div>

                {/* Driver Primary Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver Primary Number</label>
                  <input
                    type="text"
                    {...register('driverPrimaryNumber', {
                      pattern: {
                        value: /^\d{10}$/,
                        message: 'Driver Primary Number must be 10 digits'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter driver primary number"
                  />
                  {errors.driverPrimaryNumber && <p className="mt-1 text-sm text-red-600">{errors.driverPrimaryNumber.message}</p>}
                </div>

                {/* Driver Alternative Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver Alternative Number</label>
                  <input
                    type="text"
                    {...register('driverAlternativeNumber', {
                      pattern: {
                        value: /^\d{10}$/,
                        message: 'Driver Alternative Number must be 10 digits'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter driver alternative number"
                  />
                  {errors.driverAlternativeNumber && <p className="mt-1 text-sm text-red-600">{errors.driverAlternativeNumber.message}</p>}
                </div>

                {/* License Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                  <input
                    type="text"
                    {...register('licenseNumber', {
                      pattern: {
                        value: /^(([A-Z]{2}[0-9]{2})( )|([A-Z]{2}-[0-9]{2}))((19|20)[0-9][0-9])[0-9]{7}$/,
                        message: 'Enter valid License number (e.g., DL-0420160000000)'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter license number"
                  />
                  {errors.licenseNumber && <p className="mt-1 text-sm text-red-600">{errors.licenseNumber.message}</p>}
                </div>

                {/* License Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload License</label>
                  <input
                    type="file"
                    {...register('licenseImage')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    accept="image/*"
                  />
                  {errors.licenseImage && <p className="mt-1 text-sm text-red-600">{errors.licenseImage.message}</p>}
                  {licenseImage && <img src={licenseImage} alt="License Preview" className="w-32 h-20 mt-2" />}
                </div>

                {/* Aadhar Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                  <input
                    type="text"
                    {...register('aadharNumber', {
                      pattern: {
                        value: /^\d{12}$/,
                        message: 'Aadhar Number must be 12 digits'
                      }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter Aadhar number"
                  />
                  {errors.aadharNumber && <p className="mt-1 text-sm text-red-600">{errors.aadharNumber.message}</p>}
                </div>

                {/* Aadhar Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Aadhar</label>
                  <input
                    type="file"
                    {...register('aadharImage')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    accept="image/*"
                  />
                  {errors.aadharImage && <p className="mt-1 text-sm text-red-600">{errors.aadharImage.message}</p>}
                  {aadharImage && <img src={aadharImage} alt="Aadhar Preview" className="w-32 h-20 mt-2" />}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 mt-4">
              {/* Reference 1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference 1</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      {...register('reference1Name')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter Reference 1 Name"
                    />
                    {errors.reference1Name && <p className="mt-1 text-sm text-red-600">{errors.reference1Name.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="text"
                      {...register('reference1PhoneNumber')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter Reference 1 Phone Number"
                    />
                    {errors.reference1PhoneNumber && <p className="mt-1 text-sm text-red-600">{errors.reference1PhoneNumber.message}</p>}
                  </div>
                </div>
              </div>

              {/* Reference 2 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference 2</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      {...register('reference2Name')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter Reference 2 Name"
                    />
                    {errors.reference2Name && <p className="mt-1 text-sm text-red-600">{errors.reference2Name.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="text"
                      {...register('reference2PhoneNumber')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter Reference 2 Phone Number"
                    />
                    {errors.reference2PhoneNumber && <p className="mt-1 text-sm text-red-600">{errors.reference2PhoneNumber.message}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Submit Registration
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleFormData;